import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AppShell } from '../components/AppShell'
import { apiErrorMessage, useBets, useCancelBet, type Bet } from '../lib/api'

type FilterTab = 'open' | 'settled' | 'all'

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IE', { month: 'short', day: '2-digit' })
}
function fmtEur(v: string | number): string {
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n)
}

const STATUS_CHIP: Record<Bet['status'], { label: string; cls: string }> = {
  pending:    { label: 'Pending',    cls: 'chip--pending' },
  won:        { label: 'Won',        cls: 'chip--won' },
  lost:       { label: 'Lost',       cls: 'chip--lost' },
  void:       { label: 'Void',       cls: 'chip--void' },
  cancelled:  { label: 'Cancelled',  cls: 'chip--void' },
  cashed_out: { label: 'Cashed out', cls: 'chip--won' },
}

export function BetsPage() {
  const bets = useBets()
  const cancel = useCancelBet()
  const nav = useNavigate()
  const [tab, setTab] = useState<FilterTab>('open')

  const all = bets.data ?? []
  const open    = useMemo(() => all.filter((b) => b.status === 'pending'), [all])
  const settled = useMemo(() => all.filter((b) => b.status !== 'pending' && b.status !== 'cancelled'), [all])
  const visible = tab === 'open' ? open : tab === 'settled' ? settled : all

  const wins   = settled.filter((b) => b.status === 'won').length
  const losses = settled.filter((b) => b.status === 'lost').length
  const voids  = settled.filter((b) => b.status === 'void').length
  const winRate = settled.length > 0 ? (wins / settled.length) * 100 : 0
  const netPnl  = settled.reduce((acc, b) => acc + Number(b.pnl || 0), 0)
  const avgPnl  = settled.length > 0 ? netPnl / settled.length : 0
  // Aggregate CLV across settled bets (simple mean of present samples — the
  // backend stores the stake-weighted value per row; an aggregate weighted by
  // stake would need extra fields). Null-only set ⇒ undefined avg.
  const clvSamples = settled.map((b) => b.clvPct).filter((v): v is number => v !== null && Number.isFinite(v))
  const avgClv = clvSamples.length > 0 ? clvSamples.reduce((a, b) => a + b, 0) / clvSamples.length : null

  return (
    <AppShell page="bets" breadcrumb={<span className="topbar__breadcrumb-current">My Bets</span>}>
      <header className="page-header">
        <div className="page-header__main">
          <p className="page-header__eyebrow"><span>My Bets</span></p>
          <h1 className="page-header__title">My Bets</h1>
          <p className="page-header__subtitle">All bets across your active challenge.</p>
        </div>
        <div className="page-header__actions">
          <Link to="/sportsbook" className="btn btn--primary" style={{ textDecoration: 'none' }}>
            <svg className="btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12 h18 M12 3 a14 14 0 0 1 0 18 M12 3 a14 14 0 0 0 0 18" />
            </svg>
            Place a bet
          </Link>
        </div>
      </header>

      <section className="stats-row" aria-label="Bet history summary">
        <div className="stats-row__item">
          <span className="stats-row__label">Total bets</span>
          <span className="stats-row__value">{all.length}</span>
          <span className="stats-row__sub">{open.length} open · {settled.length} settled</span>
        </div>
        <div className="stats-row__item">
          <span className="stats-row__label">Win rate</span>
          <span className="stats-row__value">{settled.length > 0 ? `${winRate.toFixed(1)}%` : '—'}</span>
          <span className="stats-row__sub">{wins} W · {losses} L · {voids} V</span>
        </div>
        <div className="stats-row__item">
          <span className="stats-row__label">Net P&amp;L</span>
          <span className={`stats-row__value ${netPnl >= 0 ? 'stats-row__value--positive' : 'stats-row__value--negative'}`}>
            {netPnl >= 0 ? '+' : ''}{fmtEur(netPnl)}
          </span>
          <span className="stats-row__sub">avg. {avgPnl >= 0 ? '+' : ''}{fmtEur(avgPnl)} / bet</span>
        </div>
        <div className="stats-row__item">
          <span className="stats-row__label">Pending stake</span>
          <span className="stats-row__value">{fmtEur(open.reduce((a, b) => a + Number(b.stake || 0), 0))}</span>
          <span className="stats-row__sub">across {open.length} open bets</span>
        </div>
        <div className="stats-row__item">
          <span className="stats-row__label">Avg CLV</span>
          <span className={`stats-row__value ${avgClv != null ? (avgClv >= 0 ? 'stats-row__value--positive' : 'stats-row__value--negative') : ''}`}>
            {avgClv != null ? `${avgClv >= 0 ? '+' : ''}${(avgClv * 100).toFixed(2)}%` : '—'}
          </span>
          <span className="stats-row__sub">{clvSamples.length} settled with CLV</span>
        </div>
      </section>

      <nav className="tabs" aria-label="Bet status">
        <button type="button" className={`tab ${tab === 'open' ? 'tab--active' : ''}`} onClick={() => setTab('open')}>
          Open<span className="tab__count">{open.length}</span>
        </button>
        <button type="button" className={`tab ${tab === 'settled' ? 'tab--active' : ''}`} onClick={() => setTab('settled')}>
          Settled<span className="tab__count">{settled.length}</span>
        </button>
        <button type="button" className={`tab ${tab === 'all' ? 'tab--active' : ''}`} onClick={() => setTab('all')}>
          All<span className="tab__count">{all.length}</span>
        </button>
      </nav>

      <section className="table-card" aria-label="Bets">
        {bets.isError && (
          <div className="auth-card__error" role="alert" style={{ display: 'flex', margin: 'var(--space-4)' }}>
            {apiErrorMessage(bets.error)}
          </div>
        )}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th><th>Bet ID</th><th>Match</th><th>Pick</th>
                <th className="is-numeric">Odds</th><th className="is-numeric">Stake</th>
                <th>Status</th><th className="is-numeric">P&amp;L</th>
                <th className="is-numeric" title="Closing Line Value — odds taken vs market close">CLV</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: 'var(--space-5)' }}>
                  {bets.isLoading ? 'Loading…' : 'No bets to show.'}
                </td></tr>
              )}
              {visible.map((b) => {
                const chip = STATUS_CHIP[b.status]
                const pnl = Number(b.pnl || 0)
                const pnlSign = pnl > 0 ? 'pnl-value--positive' : pnl < 0 ? 'pnl-value--negative' : ''
                return (
                  <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => nav(`/bets/${b.id}`)}>
                    <td className="is-numeric">{fmtDate(b.placedAt)}</td>
                    <td><span className="numeric" style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>#{b.id.slice(0, 6)}</span></td>
                    <td>
                      <div className="match-cell">
                        <span className="match-cell__teams">{b.eventLabel || (b.eventId ? b.eventId.slice(0, 12) : '—')}</span>
                        {b.sport && (<span className="match-cell__league"><span className="match-cell__league-dot" aria-hidden />{b.sport}</span>)}
                      </div>
                    </td>
                    <td><span className="pick-cell">{b.selectionName}</span></td>
                    <td className="is-numeric">{Number(b.oddsDecimal).toFixed(2)}</td>
                    <td className="is-numeric">{fmtEur(b.stake)}</td>
                    <td>
                      <span className={`chip ${chip.cls}`}>
                        <span className="chip__dot" aria-hidden />{chip.label}
                      </span>
                    </td>
                    <td className="is-numeric">
                      <span className={`pnl-value ${pnlSign}`}>
                        {pnl > 0 ? '+' : ''}{fmtEur(pnl)}
                      </span>
                    </td>
                    <td className="is-numeric">
                      {b.clvPct != null ? (
                        <span className={`clv-value ${b.clvPct >= 0 ? 'clv-value--positive' : 'clv-value--negative'}`}>
                          {b.clvPct >= 0 ? '+' : ''}{(b.clvPct * 100).toFixed(2)}%
                        </span>
                      ) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                    </td>
                    <td>
                      {b.status === 'pending' && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); cancel.mutate(b.id) }}
                          className="btn btn--ghost btn--sm" style={{ color: 'var(--color-danger)' }}>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>
    </AppShell>
  )
}
